import { useEffect, useState } from "react";
import { RelatedSearch } from "src/types/RelatedSearch";
import { fetchRelatedSearches } from "../search/semanticClient";
import {
	ErrorState,
	getSuggestionStore,
	setSuggestions,
	setSuggestionsError,
	setSuggestionsLoading,
} from "../stores/suggestionStore";

/**
 * Hook to fetch and manage related search suggestions
 * @param query - The search query to get suggestions for
 * @param limit - Maximum number of suggestions to fetch (default: 10)
 * @returns Suggestions state including suggestions array, loading state, and error
 */
export function useSuggestions(query?: string, limit: number = 10) {
	const [suggestions, setSuggestionsState] = useState<RelatedSearch[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<ErrorState | undefined>();

	// Subscribe to the suggestion store for cross-island reactivity
	useEffect(() => {
		const store = getSuggestionStore();
		const unsubscribe = store.subscribe((state) => {
			setSuggestionsState(state.suggestions || []);
			setIsLoading(state.isLoading || false);
			setError(state.error);
		});
		return unsubscribe;
	}, []);

	// Fetch suggestions when query changes (query is optional - API returns results even without it)
	useEffect(() => {
		let cancelled = false;

		const fetchSuggestions = async () => {
			setSuggestionsLoading(query || "");

			const { results, error } = await fetchRelatedSearches(query, limit);

			if (cancelled) return;

			if (error) {
				setSuggestionsError(query || "", error);
			} else {
				setSuggestions(results, query);
			}
		};

		fetchSuggestions();

		return () => {
			cancelled = true;
		};
	}, [query, limit]);

	return {
		suggestions,
		isLoading,
		error,
		currentQuery: query,
	};
}
